/**
 * Карточка сделки: сделка, её лид и всё, что с ними было, одной лентой —
 * звонки (с расшифровкой и записью), чаты, дела, смены стадий, комментарии.
 * Спека `docs/specs/2026-09-25-deal-card-design.md`; те же данные —
 * `prokat-runner deal-card ID`.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router";

import { Stat, Timeline, dt } from "@/components/timeline/Timeline";
import { type ContactRow, type DealCard, loadDealCard } from "@/lib/dealCard";
import { NavMenu } from "@/nav/NavMenu";
import { DealBillingTab } from "@/pages/DealBilling";
import { DealContractTab } from "@/pages/DealContract";
import { DealPaymentsTab } from "@/pages/DealPayments";
import { DealPodborTab } from "@/pages/DealPodbor";
import { DealBonusTab } from "@/pages/DealBonus";
import { DealPriceTab } from "@/pages/DealPrice";
import { DealZapisTab } from "@/pages/DealZapis";


function Head({ c }: { c: DealCard }) {
  const d = c.deal;
  const l = c.lead;
  return (
    <section className="grid gap-2 sm:grid-cols-2">
      <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3 text-[12.5px]" data-annot="сделка">
        <div className="mb-1 text-[13px] font-medium">
          Сделка <span className="font-mono">{d.id}</span> · {d.title}
        </div>
        <div className="flex flex-wrap gap-x-3">
          <NavMenu r={{ obj: "dealID", id: d.id }} />
          {l && <NavMenu r={{ obj: "leadID", id: l.id }} />}
        </div>
        <div className="text-fg-muted">
          {d.stage ?? "—"} · {d.assigned ?? "—"} · создана {dt(d.created)}
        </div>
        <div className="text-fg-muted">
          визит {dt(d.visit)} · сумма {d.sum ?? "—"}
        </div>
      </div>
      <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3 text-[12.5px]" data-annot="лид сделки">
        {l ? (
          <>
            <div className="mb-1 text-[13px] font-medium">
              Лид <span className="font-mono">{l.id}</span> · {l.title ?? ""}
            </div>
            <div className="text-fg-muted">
              {l.source ?? "—"} · {l.status ?? "—"} · создан {dt(l.created)}
            </div>
          </>
        ) : (
          <div className="text-fg-muted">Сделка создана не из лида.</div>
        )}
        <div className="font-mono text-fg-muted">{c.phones.join(", ") || "номера нет"}</div>
      </div>
    </section>
  );
}


function Contacts({ rows }: { rows: ContactRow[] }) {
  if (!rows.length) return null;
  const cell = (items: { id: number; name?: string | null }[]) =>
    items.length ? items.map((x) => (x.name ? `${x.id} ${x.name}` : String(x.id))).join(", ") : "—";
  return (
    <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border" data-annot="контакты">
      <table className="w-full text-[12.5px]">
        <thead className="text-fg-faint">
          <tr>
            <th className="px-2.5 py-1.5 text-left font-normal">Номер</th>
            <th className="px-2.5 py-1.5 text-left font-normal" title="контакты сделки в Bitrix и b24Deal_contact">Контакт Bitrix</th>
            <th className="px-2.5 py-1.5 text-left font-normal" title="PHONE лида сделки">Лид</th>
            <th className="px-2.5 py-1.5 text-left font-normal" title="sCustomer_dogovor.customerPhone">Договор</th>
            <th className="px-2.5 py-1.5 text-left font-normal" title="sCustomer_contact клиентов сделки">Клиент салона</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.phone} className={`border-t border-border ${r.lonely ? "bg-warn-bg" : ""}`}>
              <td className="px-2.5 py-1.5">
                <Link className="underline" to={`/search?q=${r.phone}`} title="где ещё этот номер">{r.phone}</Link>
                {r.lonely && <span className="ml-1.5 text-warn" title="номер только в одном месте">только здесь</span>}
              </td>
              <td className="px-2.5 py-1.5">{cell(r.bitrix)}</td>
              <td className="px-2.5 py-1.5">{cell(r.lead)}</td>
              <td className="px-2.5 py-1.5">{cell(r.contract)}</td>
              <td className="px-2.5 py-1.5">{cell(r.customer)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Body({ c }: { c: DealCard }) {
  const s = c.summary;
  const sl = c.salon;
  return (
    <>
      <Head c={c} />
      {c.errors.length > 0 && (
        <div className="rounded-[var(--radius-card)] border border-warn p-2 text-[12.5px] text-warn">
          Карточка неполная: {c.errors.join("; ")}
        </div>
      )}
      <Contacts rows={c.contacts ?? []} />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
        <Stat label="Исходящих" value={s.calls_out} hint={`с разговором ${s.calls_out_talk}`} />
        <Stat label="Входящих разговоров" value={s.calls_in_talk} />
        <Stat label="Чатов" value={s.chats} hint="открытые линии Bitrix" />
        <Stat label="Мессенджеры" value={s.messenger_chats} hint={`чатов · сообщений ${s.messenger_msgs}`} />
        <Stat label="Звонков по номеру" value={s.calls_by_phone} hint="без привязки к лиду и сделке" />
        <Stat label="Первый контакт" value={s.first_contact ? dt(s.first_contact) : "не было"} hint="разговор или ответ в чате" />
      </div>
      {sl && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7" data-annot="итог салона">
          <Stat label="Приходов в салон" value={sl.visits} hint={`повторных примерок ${sl.repeat_visits}`} />
          <Stat label="Сердечек" value={sl.hearts} hint={`понравилось ${sl.hearts_liked}`} />
          <Stat label="Примерок запланировано" value={sl.fittings_planned} />
          <Stat label="Договор" value={sl.contract ?? "нет"} />
          <Stat
            label="Тревожных правок"
            value={<span className={sl.contract_alerts ? "text-err" : ""}>{sl.contract_alerts}</span>}
            hint="удаление, снижение цены, даты — в день выдачи и позже; цена ниже мин."
          />
          <Stat label="Оплачено" value={`${sl.paid.toLocaleString("ru-RU")} ₽`} />
          <Stat
            label="Выдано / возвращено"
            value={`${sl.issued} / ${sl.returned}`}
            hint={sl.fact_start ? `факт: ${dt(sl.fact_start)} – ${sl.fact_end ? dt(sl.fact_end) : "на руках"}` : "товаров"}
          />
        </div>
      )}
      <Timeline events={c.events} />
    </>
  );
}

/** Вкладки карточки: `?tab=bonus` — премия (спека `docs/specs/2026-09-25-deal-bonus-design.md`),
 * `?tab=billing` — биллинг (`docs/specs/2026-09-25-deal-billing-design.md`),
 * `?tab=price` — цена (`docs/specs/2026-09-25-deal-price-design.md`),
 * `?tab=contract` / `?tab=payments` — договор и оплата (`docs/specs/2026-09-25-deal-contract-payments-design.md`),
 * `?tab=zapis` — запись и загрузка часа (`docs/specs/2026-09-25-zapis-slots-design.md`),
 * `?tab=podbor` — подбор по фото клиента (`docs/specs/2026-10-07-chat-podbor-design.md`). */
type Tab = "timeline" | "contract" | "payments" | "bonus" | "billing" | "price" | "zapis" | "podbor";
const TABS: { id: Tab; label: string }[] = [
  { id: "timeline", label: "Хронология" },
  { id: "contract", label: "Договор" },
  { id: "payments", label: "Оплата" },
  { id: "bonus", label: "Премия" },
  { id: "billing", label: "Биллинг" },
  { id: "price", label: "Цена" },
  { id: "zapis", label: "Запись" },
  { id: "podbor", label: "Подбор" },
];

export function DealCardPage() {
  const dealId = Number(useParams().dealId) || 0;
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.find((t) => t.id === params.get("tab"))?.id ?? "timeline";
  const select = (id: Tab) => {
    const out = new URLSearchParams(params);
    out.delete("group"); // группировка — своя у каждой вкладки
    if (id === "timeline") out.delete("tab");
    else out.set("tab", id);
    setParams(out, { replace: true });
  };
  const q = useQuery({
    queryKey: ["deal-card", dealId],
    queryFn: () => loadDealCard(dealId),
    enabled: dealId > 0 && tab === "timeline",
  });
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      {dealId > 0 && (
        <div role="tablist" aria-label="Разделы карточки" className="flex gap-1 border-b border-border" data-annot="вкладки карточки">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => select(t.id)}
              className={`-mb-px border-b-2 px-3 pb-2 pt-1 text-[13px] ${
                tab === t.id ? "border-accent font-medium text-fg" : "border-transparent text-fg-muted hover:text-fg"
              }`}
              data-annot={`вкладка «${t.label}»`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      {!dealId ? (
        <div className="text-[12.5px] text-fg-muted">Введите номер сделки вверху, в поле «Сделка №», — покажу её лид, звонки, чаты, дела и смены стадий.</div>
      ) : tab === "contract" ? (
        <DealContractTab dealId={dealId} />
      ) : tab === "payments" ? (
        <DealPaymentsTab dealId={dealId} />
      ) : tab === "bonus" ? (
        <DealBonusTab dealId={dealId} />
      ) : tab === "billing" ? (
        <DealBillingTab dealId={dealId} />
      ) : tab === "price" ? (
        <DealPriceTab dealId={dealId} />
      ) : tab === "zapis" ? (
        <DealZapisTab dealId={dealId} />
      ) : tab === "podbor" ? (
        <DealPodborTab dealId={dealId} />
      ) : q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Собираю из Bitrix и CRM…</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">Не удалось открыть сделку: {String(q.error.message)}</div>
      ) : (
        <Body c={q.data} />
      )}
    </main>
  );
}
